import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  FileText,
  Download,
  Trash2,
  ArrowLeft,
  Calendar,
  Building2,
  Hash,
  IndianRupee,
  Loader2,
  FileSpreadsheet,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle2,
  Clock,
  Save,
  Lock,
  TrendingUp,
  Copy,
  Lightbulb
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvoiceDetailPage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [exportType, setExportType] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
      return;
    }
    
    if (isAuthenticated) {
      fetchInvoiceDetail();
    }
  }, [isAuthenticated, authLoading, invoiceId]);

  const fetchInvoiceDetail = async () => {
    try {
      const response = await axios.get(`${API}/invoices/history/${invoiceId}`, {
        withCredentials: true
      });
      setInvoice(response.data);
    } catch (error) {
      console.error("Failed to fetch invoice:", error);
      toast.error("Invoice not found");
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    setExportType("pdf");
    
    try {
      const response = await axios.post(
        `${API}/invoice/export/pdf`,
        invoice,
        { responseType: "blob", withCredentials: true }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice-report-${invoiceId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF report downloaded!");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF");
    } finally {
      setIsExporting(false);
      setExportType(null);
    }
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    setExportType("excel");
    
    try {
      const response = await axios.post(
        `${API}/invoice/export/excel`,
        invoice,
        { responseType: "blob", withCredentials: true }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice-report-${invoiceId.slice(0, 8)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Excel report downloaded!");
    } catch (error) {
      console.error("Excel export error:", error);
      toast.error("Failed to export Excel");
    } finally {
      setIsExporting(false);
      setExportType(null);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this invoice?")) return;
    
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/invoices/history/${invoiceId}`, {
        withCredentials: true
      });
      toast.success("Invoice deleted");
      navigate('/dashboard');
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete invoice");
      setIsDeleting(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "—";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: invoice?.extracted_data?.currency || "INR",
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getInsightTag = (insight) => {
    const tags = {
      duplicate: { text: "Possible duplicate", color: "#EF4444", bg: "#FEE2E2" },
      anomaly: { text: "Price anomaly", color: "#F59E0B", bg: "#FEF3C7" },
      mismatch: { text: "Mismatch detected", color: "#EF4444", bg: "#FEE2E2" },
      high_cost: { text: "High value item", color: "#6366F1", bg: "#E0E7FF" },
      missing: { text: "Missing field", color: "#9CA3AF", bg: "#F3F4F6" }
    };
    return tags[insight.type] || { text: insight.type, color: "#6B7280", bg: "#F3F4F6" };
  };

  const getSeverityIcon = (severity, type) => {
    if (type === 'duplicate') return <Copy className="w-4 h-4" />;
    if (type === 'mismatch') return <AlertTriangle className="w-4 h-4" />;
    if (type === 'high_cost') return <TrendingUp className="w-4 h-4" />;
    
    switch (severity) {
      case "error": return <AlertCircle className="w-4 h-4" />;
      case "warning": return <AlertTriangle className="w-4 h-4" />;
      default: return <Info className="w-4 h-4" />;
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
      </div>
    );
  }

  if (!invoice) return null;

  const data = invoice.extracted_data || {};
  const insights = invoice.insights || [];

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-6 sm:py-8" data-testid="invoice-detail-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-1 text-sm text-[#6B7280] hover:text-[#111827] mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
            <h1 className="text-xl sm:text-2xl font-bold text-[#111827]">
              Invoice Details
            </h1>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportPDF}
              disabled={isExporting}
              className="btn-secondary flex items-center gap-2 text-sm py-2"
            >
              {isExporting && exportType === "pdf" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              PDF
            </button>
            <button
              onClick={handleExportExcel}
              disabled={isExporting}
              className="btn-secondary flex items-center gap-2 text-sm py-2"
            >
              {isExporting && exportType === "excel" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-4 h-4" />
              )}
              Excel
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="p-2 rounded-xl text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors border border-[#FEE2E2]"
            >
              {isDeleting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Trash2 className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          <div className="card-surface p-4 col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-[#2563EB]" />
              <span className="text-xs text-[#6B7280] uppercase">Vendor</span>
            </div>
            <p className="text-sm font-semibold text-[#111827] truncate">
              {data.vendor_name || "Not detected"}
            </p>
          </div>

          <div className="card-surface p-4">
            <div className="flex items-center gap-2 mb-2">
              <IndianRupee className="w-4 h-4 text-[#10B981]" />
              <span className="text-xs text-[#6B7280] uppercase">Total</span>
            </div>
            <p className="text-sm font-semibold text-[#111827]">
              {formatCurrency(data.total_amount)}
            </p>
          </div>

          <div className="card-surface p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-[#7C3AED]" />
              <span className="text-xs text-[#6B7280] uppercase">Date</span>
            </div>
            <p className="text-sm font-semibold text-[#111827]">
              {data.invoice_date || "Not detected"}
            </p>
          </div>

          <div className="card-surface p-4">
            <div className="flex items-center gap-2 mb-2">
              <Hash className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-xs text-[#6B7280] uppercase">Invoice #</span>
            </div>
            <p className="text-sm font-semibold text-[#111827]">
              {data.invoice_number || "Not detected"}
            </p>
          </div>

          <div className="card-surface p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
              <span className="text-xs text-[#6B7280] uppercase">Confidence</span>
            </div>
            <p className="text-sm font-semibold text-[#111827]">
              {invoice.confidence_score}%
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Insights */}
          <div className="lg:col-span-1 space-y-4">
            {insights.length > 0 && (
              <div className="card-surface p-5">
                <h2 className="text-base font-semibold text-[#111827] mb-4 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-[#6366F1]" />
                  Insights ({insights.length})
                </h2>
                <div className="space-y-2">
                  {insights.map((insight, i) => {
                    const tag = getInsightTag(insight);
                    return (
                      <div key={i} className="p-3 rounded-lg bg-[#F9FAFB] border border-[#F3F4F6]">
                        <div className="flex items-start gap-2">
                          <span style={{ color: tag.color }}>{getSeverityIcon(insight.severity, insight.type)}</span>
                          <div>
                            <span 
                              className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded"
                              style={{ backgroundColor: tag.bg, color: tag.color }}
                            >
                              {tag.text}
                            </span>
                            <p className="text-sm text-[#111827] mt-1">{insight.message}</p>
                            {insight.details && (
                              <p className="text-xs text-[#6B7280] mt-0.5">{insight.details}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {insights.length === 0 && (
              <div className="card-surface p-5 text-center">
                <CheckCircle2 className="w-10 h-10 text-[#10B981] mx-auto mb-2" />
                <p className="text-sm font-medium text-[#065F46]">No issues detected!</p>
              </div>
            )}
          </div>

          {/* Line Items */}
          <div className="lg:col-span-2">
            <div className="card-surface overflow-hidden">
              <div className="p-4 border-b border-[#F3F4F6]">
                <h2 className="text-base font-semibold text-[#111827] flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#2563EB]" />
                  Line Items ({data.items?.length || 0})
                </h2>
              </div>

              {data.items && data.items.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Unit Price</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.map((item, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="text-right text-[#6B7280]">{item.quantity || "—"}</TableCell>
                        <TableCell className="text-right text-[#6B7280]">{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(item.total_price)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="p-8 text-center text-[#6B7280]">No line items detected</div>
              )}

              {/* Totals */}
              {data.total_amount && (
                <div className="border-t border-[#F3F4F6] p-4 bg-[#F9FAFB]">
                  <div className="space-y-1 max-w-xs ml-auto text-sm">
                    {data.subtotal && (
                      <div className="flex justify-between">
                        <span className="text-[#6B7280]">Subtotal</span>
                        <span>{formatCurrency(data.subtotal)}</span>
                      </div>
                    )}
                    {data.tax_amount && (
                      <div className="flex justify-between">
                        <span className="text-[#6B7280]">Tax</span>
                        <span>{formatCurrency(data.tax_amount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold pt-1 border-t border-[#E5E7EB]">
                      <span>Total</span>
                      <span className="text-[#2563EB]">{formatCurrency(data.total_amount)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
